import React from "react";
import { Link } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import PosterArt from "@/components/media/PosterArt";
import { cn } from "@/lib/utils";

export default function ContentCard({ title, variant = "poster", inList = false, onToggleList, className = "", showBadge = false }) {
  const isBackdrop = variant === "backdrop";
  const posterUrl = isBackdrop ? title.backdrop_url : title.poster_url;
  const aspectClass = isBackdrop ? "aspect-video" : "aspect-[2/3]";
  const badgeLabel = title.is_original ? "ORIGINAL" : title.type?.toUpperCase();
  return (
    <Link
      to={`/title/${title.slug}`}
      data-testid={`content-card-${title.slug}`}
      aria-label={`${title.title}${title.is_original ? " — YCN Original" : ""}`}
      className={cn(
        "group relative block overflow-hidden rounded-xl bg-[#111111] border border-transparent hover:border-white/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(124,58,237,0.5)]",
        className
      )}
    >
      {posterUrl ? (
        <div className={cn("relative w-full overflow-hidden", aspectClass)}>
          <img src={posterUrl} alt={title.title} loading="lazy" className="w-full h-full object-cover" />
        </div>
      ) : (
        <PosterArt
          title={title.title}
          subtitle={badgeLabel}
          gradient={title.poster_gradient}
          aspect={isBackdrop ? "backdrop" : "poster"}
        />
      )}

      {/* Top badges */}
      {showBadge && title.is_original && (
        <div className="absolute top-2 left-2 z-10">
          <span className="px-2 py-1 rounded-md text-[10px] font-bold tracking-widest bg-gradient-to-r from-violet-600 to-orange-500 text-white shadow-lg">YCN ORIGINAL</span>
        </div>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-black via-black/60 to-transparent flex flex-col justify-end p-4">
        <div className="text-white font-display font-bold text-base leading-tight">{title.title}</div>
        <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1 flex items-center gap-2 flex-wrap">
          {title.year && <span>{title.year}</span>}
          {title.rating && <span className="px-1 border border-white/20 rounded text-[9px]">{title.rating}</span>}
          {title.duration && <span>{title.duration}</span>}
        </div>
        <div className="mt-3 flex items-center gap-2">
          {onToggleList && (
            <button
              onClick={(e) => { e.preventDefault(); onToggleList(title); }}
              data-testid={`card-mylist-${title.slug}`}
              className="h-8 w-8 rounded-full ycn-glass hover:bg-white/20 flex items-center justify-center"
              aria-label={inList ? "Remove from My List" : "Add to My List"}
            >
              {inList ? <Check size={14} /> : <Plus size={14} />}
            </button>
          )}
        </div>
      </div>

      {/* Persistent caption below the poster */}
      <div className="px-2 pt-2 pb-2.5">
        <div className="text-white text-[13px] leading-tight font-semibold truncate" title={title.title}>
          {title.title}
        </div>
      </div>
    </Link>
  );
}
